'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import type { EdgeShape, HierarchyDirection, LayoutMode, ViewMode } from '../_schemas/graph';
import { EDGE_SHAPES, HIERARCHY_DIRECTIONS, LAYOUT_MODES, PARAM_DIRECTION, PARAM_LAYOUT, PARAM_LETTER, PARAM_SHAPE, PARAM_VIEW, VIEW_MODES } from '../_schemas/graph';

// Type guards for validation
function isValidViewMode(value: string | null): value is ViewMode {
  return value !== null && VIEW_MODES.includes(value as ViewMode);
}
function isValidLayoutMode(value: string | null): value is LayoutMode {
  return value !== null && LAYOUT_MODES.includes(value as LayoutMode);
}
function isValidHierarchyDirection(value: string | null): value is HierarchyDirection {
  return value !== null && HIERARCHY_DIRECTIONS.includes(value as HierarchyDirection);
}
function isValidEdgeShape(value: string | null): value is EdgeShape {
  return value !== null && EDGE_SHAPES.includes(value as EdgeShape);
}
function isValidLetterId(value: string | null): boolean {
  if (value === null) return false;
  const num = Number.parseInt(value, 10);
  return !Number.isNaN(num) && num > 0;
}
export interface PlaygroundSettings {
  viewMode: ViewMode;
  layoutMode: LayoutMode;
  hierarchyDirection: HierarchyDirection;
  edgeShape: EdgeShape;
  selectedLetterId: number;
}
export interface UsePlaygroundParamsOptions {
  /** Current values from store */
  currentSettings: PlaygroundSettings;
  /** Callbacks to update store */
  onViewModeChange: (mode: ViewMode) => void;
  onLayoutModeChange: (mode: LayoutMode) => void;
  onHierarchyDirectionChange: (direction: HierarchyDirection) => void;
  onEdgeShapeChange: (shape: EdgeShape) => void;
  onLetterSelect: (id: number) => void;
}

/**
 * Hook to sync playground settings with URL query params.
 * - On mount: reads query params and updates store if valid
 */
export function usePlaygroundParams(t0) {
  const $ = _c(9);
  const {
    currentSettings,
    onViewModeChange,
    onLayoutModeChange,
    onHierarchyDirectionChange,
    onEdgeShapeChange,
    onLetterSelect
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== currentSettings || $[1] !== onEdgeShapeChange || $[2] !== onHierarchyDirectionChange || $[3] !== onLayoutModeChange || $[4] !== onLetterSelect || $[5] !== onViewModeChange || $[6] !== searchParams) {
    t1 = () => {
      const viewParam = searchParams.get(PARAM_VIEW);
      const layoutParam = searchParams.get(PARAM_LAYOUT);
      const directionParam = searchParams.get(PARAM_DIRECTION);
      const shapeParam = searchParams.get(PARAM_SHAPE);
      const letterParam = searchParams.get(PARAM_LETTER);
      if (isValidViewMode(viewParam) && viewParam !== currentSettings.viewMode) {
        onViewModeChange(viewParam);
      }
      if (isValidLayoutMode(layoutParam) && layoutParam !== currentSettings.layoutMode) {
        onLayoutModeChange(layoutParam);
      }
      if (isValidHierarchyDirection(directionParam) && directionParam !== currentSettings.hierarchyDirection) {
        onHierarchyDirectionChange(directionParam);
      }
      if (isValidEdgeShape(shapeParam) && shapeParam !== currentSettings.edgeShape) {
        onEdgeShapeChange(shapeParam);
      }
      if (isValidLetterId(letterParam)) {
        const letterId = Number.parseInt(letterParam as string, 10);
        if (letterId !== currentSettings.selectedLetterId) {
          onLetterSelect(letterId);
        }
      }
    };
    $[0] = currentSettings;
    $[1] = onEdgeShapeChange;
    $[2] = onHierarchyDirectionChange;
    $[3] = onLayoutModeChange;
    $[4] = onLetterSelect;
    $[5] = onViewModeChange;
    $[6] = searchParams;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
}